import { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Button, alpha, useTheme,
} from '@mui/material';
import { Search, MessageSquare, FileText, PenTool, Monitor, Zap } from 'lucide-react';
import { analyticsApi } from '../../../../api/analyticsApi';

const TYPE_CONFIG = {
  event: { icon: <Zap size={14} />, color: '#607d8b', label: 'Event' },
  search: { icon: <Search size={14} />, color: '#4caf50', label: 'Search' },
  conversation: { icon: <MessageSquare size={14} />, color: '#2196f3', label: 'Chat' },
  contract: { icon: <FileText size={14} />, color: '#ff9800', label: 'Contract' },
  signature: { icon: <PenTool size={14} />, color: '#9c27b0', label: 'Signature' },
  session: { icon: <Monitor size={14} />, color: '#795548', label: 'Session' },
};

const UserActivityFeed = ({ userId }) => {
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await analyticsApi.getUserActivityFeed(userId, page, 50);
        if (page === 1) {
          setItems(data.items || []);
        } else {
          setItems((prev) => [...prev, ...(data.items || [])]);
        }
        setTotal(data.total || 0);
      } catch (err) {
        console.error('Failed to load activity feed:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, page]);

  const groupByDate = (items) => {
    const groups = {};
    items.forEach((item) => {
      const date = item.timestamp?.split('T')[0] || item.timestamp?.split(' ')[0] || 'Unknown';
      if (!groups[date]) groups[date] = [];
      groups[date].push(item);
    });
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  };

  const grouped = groupByDate(items);

  return (
    <Box>
      {grouped.map(([date, dayItems]) => (
        <Box key={date} sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{
            fontWeight: 600, color: theme.palette.text.secondary,
            display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            {new Date(date).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1, borderLeft: `2px solid ${theme.palette.divider}` }}>
            {dayItems.map((item, i) => {
              const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.event;
              const time = item.timestamp ? new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75, pl: 1.5 }}>
                  <Box sx={{
                    p: 0.5, borderRadius: '6px', mt: 0.25,
                    bgcolor: alpha(config.color, 0.1), color: config.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {config.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }} noWrap>
                      {item.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Chip label={config.label} size="small" sx={{
                      fontSize: '0.6rem', height: 18,
                      bgcolor: alpha(config.color, 0.08), color: config.color,
                    }} />
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                      {time}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && items.length < total && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setPage((p) => p + 1)}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            Load more ({items.length} / {total})
          </Button>
        </Box>
      )}

      {!loading && items.length === 0 && (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 3 }}>
          No activity recorded.
        </Typography>
      )}
    </Box>
  );
};

export default UserActivityFeed;
